import httpClient from './httpClient';
import { buildQueryString } from '../utils/queryBuilder';
import { parseApiError } from '../utils/errorHandler';

/**
 * @param {Object} data
 * @param {string[]} [preferredEmbeddedKeys] - chaves em _embedded a tentar após `content`
 * @returns {{ content: Array, page: Object }}
 */
function parseSpringPagedModel(data, preferredEmbeddedKeys = []) {
    if (!data || typeof data !== 'object') {
        return {
            content: [],
            page: { totalPages: 0, number: 0, first: true, last: true },
        };
    }

    let raw = null;
    if (Array.isArray(data.content)) {
        raw = data.content;
    } else {
        const emb = data._embedded || {};
        const keys = ['content', ...preferredEmbeddedKeys];
        for (const k of keys) {
            if (Array.isArray(emb[k])) {
                raw = emb[k];
                break;
            }
        }
    }

    if (!Array.isArray(raw)) {
        const emb = data._embedded || {};
        const arrays = Object.values(emb).filter((v) => Array.isArray(v));
        raw = arrays.length > 0 ? arrays[0] : [];
    }

    const content = Array.isArray(raw) ? raw : [];

    let pageMeta;
    if (data.page && typeof data.page === 'object') {
        pageMeta = data.page;
    } else {
        const totalPages =
            data.totalPages != null && data.totalPages !== ''
                ? Number(data.totalPages)
                : content.length === 0
                  ? 0
                  : 1;
        const number = data.number != null && data.number !== '' ? Number(data.number) : 0;
        pageMeta = {
            totalPages: Number.isFinite(totalPages) ? totalPages : 0,
            number: Number.isFinite(number) ? number : 0,
            first: data.first !== undefined ? Boolean(data.first) : number === 0,
            last: data.last !== undefined ? Boolean(data.last) : totalPages <= 1,
        };
    }

    return { content, page: pageMeta };
}

/**
 * Extrai lista e metadados de página de respostas Spring Data (JSON em raiz) ou HAL (_embedded).
 * @param {Object} data
 * @returns {{ content: Array, page: Object }}
 */
export function parseCostCenterPagedResponse(data) {
    return parseSpringPagedModel(data, [
        'costCenterHeaders',
        'costCenters',
        'costCenterHeaderList',
    ]);
}

/**
 * PagedModel de colaboradores vinculados ao centro de custo.
 * @param {Object} data
 * @returns {{ content: Array, page: Object }}
 */
export function parseCostCenterEmployeePagedResponse(data) {
    return parseSpringPagedModel(data, [
        'costCenterEmployees',
        'employees',
        'funcionarios',
        'employeeList',
    ]);
}

/**
 * Lista centros de custo de um cliente (pessoa jurídica).
 * Path `/pessoa-juridica/{id}/centro-de-custo`; alguns controllers também esperam `customerId` na query.
 *
 * @param {number|string} customerId - ID do cliente / PJ
 * @param {Object} params
 * @param {number} [params.page]
 * @param {number} [params.size]
 * @param {string} [params.sort] - ex.: name,asc
 * @param {string} [params.name]
 * @param {string} [params.code]
 * @param {boolean|null|undefined} [params.active] - se `undefined`, o parâmetro não é enviado (comportamento default do backend)
 * @returns {Promise<{ content: Array, page: Object }>}
 */
export async function listCostCenters(customerId, { page = 0, size = 10, sort = 'name,asc', name, code, active } = {}) {
    const cid = customerId != null ? String(customerId).trim() : '';
    if (!cid) {
        throw new Error('customerId é obrigatório para listar centros de custo.');
    }

    const idNum = Number(cid);
    const customerIdParam = Number.isFinite(idNum) && !Number.isNaN(idNum) ? idNum : cid;

    const filters = {
        customerId: customerIdParam,
    };
    if (name != null && String(name).trim() !== '') {
        filters.name = String(name).trim();
    }
    if (code != null && String(code).trim() !== '') {
        filters.code = String(code).trim();
    }
    if (active !== undefined && active !== null) {
        filters.active = Boolean(active);
    }

    const queryString = buildQueryString({ page, size, sort, filters });
    const url = `/api/pessoa-juridica/${encodeURIComponent(cid)}/centro-de-custo?${queryString}`;
    const response = await httpClient.get(url);

    if (!response.ok) {
        throw await parseApiError(response);
    }

    const data = await response.json();
    return parseCostCenterPagedResponse(data);
}

/**
 * Cria um centro de custo para o cliente (pessoa jurídica).
 * Resposta 204 com header Location `/{id}`.
 *
 * @param {number|string} customerId
 * @param {{ name: string, code?: string|null }} payload
 * @returns {Promise<{ id: number|null }>}
 */
export async function createCostCenter(customerId, payload) {
    const cid = customerId != null ? String(customerId).trim() : '';
    if (!cid) {
        throw new Error('customerId é obrigatório para criar centro de custo.');
    }
    const name = payload?.name != null ? String(payload.name).trim() : '';
    if (!name) {
        throw new Error('Nome é obrigatório.');
    }

    const url = `/api/pessoa-juridica/${encodeURIComponent(cid)}/centro-de-custo`;
    const body = {
        name,
        code: payload?.code != null && String(payload.code).trim() !== '' ? String(payload.code).trim() : null,
    };

    const response = await httpClient.post(url, body);

    if (!response.ok) {
        throw await parseApiError(response);
    }

    const location = response.headers.get('Location');
    let id = null;
    if (location) {
        const m = String(location).match(/(\d+)\s*$/);
        if (m) {
            id = Number(m[1], 10);
        }
    }
    return { id };
}

/**
 * Atualiza um centro de custo (nome, código opcional e status ativo).
 * PUT /api/pessoa-juridica/{customerId}/centro-de-custo/{costCenterId}
 *
 * @param {number|string} customerId
 * @param {number|string} costCenterId
 * @param {{ name: string, code?: string|null, active: boolean }} payload
 */
export async function updateCostCenter(customerId, costCenterId, payload) {
    const cid = customerId != null ? String(customerId).trim() : '';
    const ccid = costCenterId != null ? String(costCenterId).trim() : '';
    if (!cid || !ccid) {
        throw new Error('customerId e costCenterId são obrigatórios para atualizar centro de custo.');
    }
    const name = payload?.name != null ? String(payload.name).trim() : '';
    if (!name) {
        throw new Error('Nome é obrigatório.');
    }

    const url = `/api/pessoa-juridica/${encodeURIComponent(cid)}/centro-de-custo/${encodeURIComponent(ccid)}`;
    const body = {
        name,
        code: payload?.code != null && String(payload.code).trim() !== '' ? String(payload.code).trim() : null,
        active: Boolean(payload.active),
    };

    const response = await httpClient.put(url, body);

    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * Lista colaboradores (funcionários) de um centro de custo.
 * GET /api/pessoa-juridica/{customerId}/centro-de-custo/{costCenterId}/funcionarios
 *
 * @param {number|string} customerId
 * @param {number|string} costCenterId
 * @param {Object} [params]
 * @param {number} [params.page]
 * @param {number} [params.size]
 * @param {string} [params.sort]
 * @param {string} [params.name]
 * @param {string} [params.email]
 * @returns {Promise<{ content: Array, page: Object }>}
 */
export async function listCostCenterEmployees(
    customerId,
    costCenterId,
    { page = 0, size = 10, sort = 'name,asc', name, email } = {},
) {
    const cid = customerId != null ? String(customerId).trim() : '';
    const ccid = costCenterId != null ? String(costCenterId).trim() : '';
    if (!cid || !ccid) {
        throw new Error('customerId e costCenterId são obrigatórios para listar colaboradores do centro de custo.');
    }

    const filters = {};
    if (name != null && String(name).trim() !== '') {
        filters.name = String(name).trim();
    }
    if (email != null && String(email).trim() !== '') {
        filters.email = String(email).trim();
    }

    const queryString = buildQueryString({ page, size, sort, filters });
    const url = `/api/pessoa-juridica/${encodeURIComponent(cid)}/centro-de-custo/${encodeURIComponent(
        ccid,
    )}/funcionarios?${queryString}`;
    const response = await httpClient.get(url);

    if (!response.ok) {
        throw await parseApiError(response);
    }

    const data = await response.json();
    return parseCostCenterEmployeePagedResponse(data);
}

/**
 * Lista colaboradores elegíveis para inclusão no centro de custo.
 * GET /api/pessoa-juridica/{customerId}/centro-de-custo/{costCenterId}/funcionarios-para-inclusao
 *
 * @param {number|string} customerId
 * @param {number|string} costCenterId
 * @param {Object} [params]
 * @param {number} [params.page]
 * @param {number} [params.size]
 * @param {string} [params.sort]
 * @param {string} [params.name]
 * @param {string} [params.email]
 * @returns {Promise<{ content: Array, page: Object }>}
 */
export async function listCostCenterEligibleEmployees(
    customerId,
    costCenterId,
    { page = 0, size = 10, sort = 'name,asc', name, email } = {},
) {
    const cid = customerId != null ? String(customerId).trim() : '';
    if (!cid) {
        throw new Error('customerId é obrigatório para listar colaboradores elegíveis do centro de custo.');
    }

    const filters = {};
    if (name != null && String(name).trim() !== '') {
        filters.name = String(name).trim();
    }
    if (email != null && String(email).trim() !== '') {
        filters.email = String(email).trim();
    }

    const queryString = buildQueryString({ page, size, sort, filters });
    const url = `/api/pessoa-juridica/${encodeURIComponent(
        cid,
    )}/centro-de-custo/funcionarios-para-inclusao?${queryString}`;

    const response = await httpClient.get(url);
    if (!response.ok) {
        throw await parseApiError(response);
    }

    const data = await response.json();
    return parseCostCenterEmployeePagedResponse(data);
}

/**
 * Associa um colaborador específico a um centro de custo.
 * POST /api/pessoa-juridica/{customerId}/centro-de-custo/{costCenterId}/funcionarios?employeeId={employeeId}
 *
 * @param {number|string} customerId
 * @param {number|string} costCenterId
 * @param {number|string} employeeId
 */
export async function insertCostCenterEmployee(customerId, costCenterId, employeeId) {
    const cid = customerId != null ? String(customerId).trim() : '';
    const ccid = costCenterId != null ? String(costCenterId).trim() : '';
    const eid = employeeId != null ? String(employeeId).trim() : '';
    if (!cid || !ccid || !eid) {
        throw new Error('customerId, costCenterId e employeeId são obrigatórios para inserir colaborador.');
    }

    const url = `/api/pessoa-juridica/${encodeURIComponent(cid)}/centro-de-custo/${encodeURIComponent(
        ccid,
    )}/funcionarios?employeeId=${encodeURIComponent(eid)}`;

    const response = await httpClient.request(url, { method: 'POST' });
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * Remove um colaborador do centro de custo.
 * DELETE /api/pessoa-juridica/{customerId}/centro-de-custo/{costCenterId}/funcionarios/{employeeId}
 *
 * @param {number|string} customerId
 * @param {number|string} costCenterId
 * @param {number|string} employeeId
 */
export async function removeCostCenterEmployee(customerId, costCenterId, employeeId) {
    const cid = customerId != null ? String(customerId).trim() : '';
    const ccid = costCenterId != null ? String(costCenterId).trim() : '';
    const eid = employeeId != null ? String(employeeId).trim() : '';
    if (!cid || !ccid || !eid) {
        throw new Error('customerId, costCenterId e employeeId são obrigatórios para remover colaborador.');
    }

    const url = `/api/pessoa-juridica/${encodeURIComponent(cid)}/centro-de-custo/${encodeURIComponent(
        ccid,
    )}/funcionarios/${encodeURIComponent(eid)}`;

    const response = await httpClient.delete(url);
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

const costCenterService = {
    list: listCostCenters,
    create: createCostCenter,
    update: updateCostCenter,
    listEmployees: listCostCenterEmployees,
    listEligibleEmployees: listCostCenterEligibleEmployees,
    insertEmployee: insertCostCenterEmployee,
    removeEmployee: removeCostCenterEmployee,
    parsePagedResponse: parseCostCenterPagedResponse,
    parseEmployeePagedResponse: parseCostCenterEmployeePagedResponse,
};

export default costCenterService;
